import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";
import { Loader, Select } from "cxa-components";
import HDFLink from "./HDFLink";
import { loadBenefitStatement } from "actions/benefitStatementActions";
import messages from "components/messagesDescription";
import { formatDate, formatDateTime } from "../../../i18n/util";
const BenefitStatement = ({ intl, planYearId }) => {
  const dispatch = useDispatch();
  const { benefitStatement, error, fetching } = useSelector(state => state.newuiuxBenefits);
  const [selectedMember, setSelectedMember] = useState("");
  const [members, setMembers] = useState([]);
  const selectedPlanPeriod =
    benefitStatement.planPeriodsList &&
    benefitStatement.planPeriodsList.find(period => period.isSelected);

  useEffect(() => {
    if (planYearId) {
      dispatch(loadBenefitStatement({ defaultPlanId: planYearId }));
    }
  }, [dispatch, planYearId]);

  useEffect(() => {
    if (benefitStatement.benefits) {
      const allMembers = new Set();
      benefitStatement.benefits.forEach(benefit => {
        const membersList = []
          .concat((benefit.guaranteedPlan && benefit.guaranteedPlan.members) || [])
          .concat((benefit.pendingInsurer && benefit.pendingInsurer.members) || []);
        membersList.forEach(member => {
          allMembers.add(JSON.stringify(member));
        });
      });
      setMembers(Array.from(allMembers).map(member => JSON.parse(member)));
    }
  }, [benefitStatement]);

  const handleMemberChange = e => {
    setSelectedMember(e.target.value);
  };

  const renderMemberOptions = () => {
    return [
      <option key="all" value="">
        {intl.formatMessage(messages.benefitStatement.allMembers)}
      </option>,
      ...members.map(member => (
        <option key={`${member.type}-${member.key}`} value={`${member.type}-${member.key}`}>
          {member.name}
        </option>
      )),
    ];
  };

  const isMemberInBenefit = (benefit, memberKey) => {
    if (!memberKey) return true;
    const allMembers = []
      .concat((benefit.guaranteedPlan && benefit.guaranteedPlan.members) || [])
      .concat((benefit.pendingInsurer && benefit.pendingInsurer.members) || []);
    return allMembers.some(member => `${member.type}-${member.key}` === memberKey);
  };

  const renderMembers = members => (
    <div className="bs-members-list">
      {members.map(m => (
        <div key={m.key} className="bs-member-item">
          {m.name}
        </div>
      ))}
    </div>
  );

  const renderTableRow = (label, content) => (
    <div className="bs-table-row">
      <div className="bs-table-cell bs-label">{label}</div>
      <div className="bs-table-cell bs-content">{content}</div>
    </div>
  );

  const generateUniqueDivKey = benefit => {
    const getMemberKey = members => {
      if (members && members.length > 0) {
        return members[0].type + "-" + members[0].key;
      }
      return "";
    };

    let memberKey = "";
    if (benefit.guaranteedPlan && benefit.guaranteedPlan.members) {
      memberKey = getMemberKey(benefit.guaranteedPlan.members);
    } else if (benefit.pendingInsurer && benefit.pendingInsurer.members) {
      memberKey = getMemberKey(benefit.pendingInsurer.members);
    }

    return benefit.benefitId + "-" + (memberKey || "no-member");
  };

  const renderBenefitDetails = benefit => {
    if (!isMemberInBenefit(benefit, selectedMember)) return null;
    const divKey = generateUniqueDivKey(benefit);

    return (
      <div key={divKey} className="bs-benefit-item">
        <div className="bs-benefit-header">
          <h5>
            {intl.formatMessage({
              id: benefit.benefitName,
              defaultMessage: benefit.benefitName,
            })}
          </h5>
          <div className="bs-hdf-links">
            {benefit.completedUWForm &&
              benefit.completedUWForm.length > 0 &&
              benefit.completedUWForm.map(uw => (
                <HDFLink
                  key={uw.uwFormCode}
                  planYearId={planYearId}
                  uwFormCode={uw.uwFormCode}
                  isMobileResponsive={uw.isMobileResponsive}
                  enrolmentKey={uw.enrolmentKey}
                  completedOn={uw.completedOn}
                />
              ))}
          </div>
        </div>
        {benefit.guaranteedPlan && (
          <div className="bs-benefit-section">
            {renderTableRow(
              <h5>{intl.formatMessage(messages.benefitStatement.guaranteedCoverage)}</h5>
            )}
            {renderTableRow(
              intl.formatMessage(messages.benefitStatement.approvedCoverage),
              intl.formatMessage({
                id: benefit.guaranteedPlan.approvedPlan,
                defaultMessage: benefit.guaranteedPlan.approvedPlan,
              })
            )}
            {benefit.guaranteedPlan.members &&
              renderTableRow(
                intl.formatMessage(messages.benefitStatement.coveredMembers),
                renderMembers(benefit.guaranteedPlan.members)
              )}
            {benefit.guaranteedPlan.guaranteedAcceptance &&
              renderTableRow(
                intl.formatMessage(messages.benefitStatement.approvedSumAssured),
                benefit.guaranteedPlan.guaranteedAcceptance
              )}
          </div>
        )}
        {benefit.selectedPlan && (
          <div className="bs-benefit-section">
            {renderTableRow(
              <h5>{intl.formatMessage(messages.benefitStatement.selectedCoverage)}</h5>,
              intl.formatMessage({
                id: benefit.selectedPlan.selectedPlan,
                defaultMessage: benefit.selectedPlan.selectedPlan,
              })
            )}
            {benefit.selectedPlan.selectedSA &&
              renderTableRow(
                intl.formatMessage(messages.benefitStatement.selectedSumAssured),
                benefit.selectedPlan.selectedSA
              )}
            {renderTableRow(
              intl.formatMessage(messages.benefitStatement.selectedOn),
              formatDateTime(benefit.selectedPlan.selectedDate)
            )}
            {benefit.selectedPlan.uwDecision &&
              renderTableRow(
                intl.formatMessage(messages.benefitStatement.underwritingDecision),
                benefit.selectedPlan.uwDecision
              )}
          </div>
        )}
        {benefit.pendingInsurer && (
          <div className="bs-benefit-section">
            {renderTableRow(
              <h5>{intl.formatMessage(messages.benefitStatement.pendingInsurer)}</h5>
            )}
            {benefit.type === "SumAssuredBased"
              ? renderTableRow(
                  intl.formatMessage(messages.benefitStatement.pendingSumAssured),
                  benefit.pendingInsurer.pendingAcceptance
                )
              : renderTableRow(
                  intl.formatMessage(messages.benefitStatement.pendingCoverage),
                  benefit.pendingInsurer.pendingAcceptance
                )}
            {benefit.pendingInsurer.members &&
              renderTableRow(
                intl.formatMessage(messages.benefitStatement.pendingAcceptanceFor),
                renderMembers(benefit.pendingInsurer.members)
              )}
          </div>
        )}
      </div>
    );
  };

  if (fetching) return <Loader />;

  if (error) return <p>{error.message}</p>;

  return (
    <div className="bs-benefit-statement">
      {benefitStatement.benefitStatementConfirmMessage && (
        <div
          className="alert alert--info"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: "config.benefitConfirmMsg",
              defaultMessage: benefitStatement.benefitStatementConfirmMessage,
            }),
          }}
        />
      )}

      <header className="bs-statement-header">
        <section>
          {`${intl.formatMessage(messages.periodDescription)}:
            ${formatDate(selectedPlanPeriod && selectedPlanPeriod.startDate)} -
            ${formatDate(selectedPlanPeriod && selectedPlanPeriod.endDate)}`}
        </section>
        <section>
          {`${intl.formatMessage(messages.statementDateDescription)}:
            ${formatDate(benefitStatement.statementDate)}`}
        </section>
      </header>

      <div className="bs-statement-content">
        <div className="bs-member-filter">
          <Select onChange={handleMemberChange} value={selectedMember}>
            {renderMemberOptions()}
          </Select>
        </div>

        {benefitStatement.benefits && benefitStatement.benefits.map(renderBenefitDetails)}

        {benefitStatement.otherBenefits && benefitStatement.otherBenefits.length > 0 && (
          <div className="bs-other-benefits">
            <h3>{intl.formatMessage(messages.benefitStatement.otherBenefits)}</h3>
            {benefitStatement.otherBenefits.map(renderBenefitDetails)}
          </div>
        )}
      </div>

      {benefitStatement.noteForProduct && (
        <div
          className="bs-product-note"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: "config.benefitFootNote",
              defaultMessage: benefitStatement.noteForProduct,
            }),
          }}
        />
      )}
    </div>
  );
};

BenefitStatement.propTypes = {
  intl: intlShape.isRequired,
  planYearId: PropTypes.string.isRequired,
};

export default injectIntl(BenefitStatement);



// WEBPACK FOOTER //
// ./components/employee/benefits/BenefitStatement.js